import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Star, Trash2, ArrowUp, ArrowDown, MapPin } from "lucide-react";
import { adminGetSettings, adminPutSettings } from "@/lib/api";
import { adminInputCls, adminBtnCls, adminGoldBtnCls } from "./shared";

interface Area {
  name: string;
  featured: boolean;
}

export const AreasTab = ({ onAuthError }: { onAuthError: () => void }) => {
  const [areas, setAreas] = useState<Area[] | null>(null);
  const [newCity, setNewCity] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetSettings()
      .then((r) => setAreas(r.data.service_areas || []))
      .catch((e) => {
        if (e?.response?.status === 401) onAuthError();
        else setAreas([]);
      });
  }, [onAuthError]);

  const move = (idx: number, dir: -1 | 1) => {
    if (!areas) return;
    const target = idx + dir;
    if (target < 0 || target >= areas.length) return;
    const copy = [...areas];
    [copy[idx], copy[target]] = [copy[target], copy[idx]];
    setAreas(copy);
  };

  const save = async () => {
    if (!areas) return;
    setSaving(true);
    try {
      await adminPutSettings({ service_areas: areas });
      toast.success("Service areas saved — public site and AI assistant updated");
    } catch (e: any) {
      if (e?.response?.status === 401) onAuthError();
      else toast.error("Save failed");
    }
    setSaving(false);
  };

  if (areas === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div data-testid="admin-areas-tab">
      <p className="text-sm text-neutral-400">
        These cities appear in the public Service Area section and in the Ask R3 assistant's
        answers. They are <span className="font-bold text-gold">service areas only</span> — never
        add office addresses.
      </p>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <input
          data-testid="area-new-input"
          value={newCity}
          onChange={(e) => setNewCity(e.target.value)}
          placeholder="Add a city…"
          className={adminInputCls}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (newCity.trim()) {
                setAreas([...areas, { name: newCity.trim(), featured: false }]);
                setNewCity("");
              }
            }
          }}
        />
        <button
          data-testid="area-add-btn"
          onClick={() => {
            if (newCity.trim()) {
              setAreas([...areas, { name: newCity.trim(), featured: false }]);
              setNewCity("");
            }
          }}
          className={adminGoldBtnCls}
        >
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>

      <ul className="mt-6 space-y-3" data-testid="area-list">
        {areas.map((a, i) => (
          <li
            key={`${a.name}-${i}`}
            data-testid={`area-row-${i}`}
            className="flex items-center justify-between gap-3 border border-white/10 bg-charcoal px-5 py-4"
          >
            <span className="flex items-center gap-2.5 text-sm font-bold text-white">
              <MapPin className={`h-4 w-4 ${a.featured ? "text-gold" : "text-neutral-500"}`} />
              {a.name}
              {a.featured && (
                <span className="rounded-full border border-gold/40 bg-gold/10 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-[0.15em] text-gold">
                  Featured
                </span>
              )}
            </span>
            <span className="flex items-center gap-1.5">
              <button
                data-testid={`area-feature-${i}`}
                onClick={() => {
                  const copy = [...areas];
                  copy[i] = { ...copy[i], featured: !copy[i].featured };
                  setAreas(copy);
                }}
                className={`p-2 transition-colors duration-200 ${a.featured ? "text-gold" : "text-neutral-600 hover:text-gold"}`}
                aria-label="Toggle featured"
              >
                <Star className={`h-4 w-4 ${a.featured ? "fill-gold" : ""}`} />
              </button>
              <button data-testid={`area-up-${i}`} onClick={() => move(i, -1)} className="p-2 text-neutral-500 transition-colors duration-200 hover:text-gold" aria-label="Move up">
                <ArrowUp className="h-4 w-4" />
              </button>
              <button data-testid={`area-down-${i}`} onClick={() => move(i, 1)} className="p-2 text-neutral-500 transition-colors duration-200 hover:text-gold" aria-label="Move down">
                <ArrowDown className="h-4 w-4" />
              </button>
              <button
                data-testid={`area-remove-${i}`}
                onClick={() => setAreas(areas.filter((_, j) => j !== i))}
                className="p-2 text-neutral-500 transition-colors duration-200 hover:text-red-400"
                aria-label="Remove city"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </span>
          </li>
        ))}
      </ul>

      <button data-testid="areas-save-btn" onClick={save} disabled={saving} className={`${adminGoldBtnCls} mt-6`}>
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Save Service Areas
      </button>
    </div>
  );
};
